import { useState, useEffect } from 'react';
import paymentGatewayService from '../services/paymentGatewayService';

export const usePaymentGateways = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGateways = async () => {
    setLoading(true);
    try {
      const { data, error } = await paymentGatewayService.getAllGateways();
      if (error) {
        setError(error);
      } else {
        setGateways(data);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateGateway = async (gatewayId, config) => {
    try {
      const { data, error } = await paymentGatewayService.updateGateway(gatewayId, config);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      // Update local state
      setGateways(prev => 
        prev.map(gateway => 
          gateway.id === gatewayId ? { ...gateway, ...config } : gateway
        )
      );
      
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const testConnection = async (gatewayId, config) => {
    try {
      const result = await paymentGatewayService.testGatewayConnection(gatewayId, config);
      return result;
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  return {
    gateways,
    loading,
    error,
    refetch: fetchGateways,
    updateGateway,
    testConnection
  };
};

export const usePaymentMethods = (gatewayId) => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMethods = async () => {
      if (!gatewayId) return;
      
      setLoading(true);
      try {
        const { data } = await paymentGatewayService.getPaymentMethods(gatewayId);
        setMethods(data || []);
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMethods();
  }, [gatewayId]);

  return { methods, loading };
};

export const usePaymentAnalytics = (gatewayId, period = '30d') => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!gatewayId) return;
      
      setLoading(true);
      try {
        const { data } = await paymentGatewayService.getAnalytics(gatewayId, period);
        setAnalytics(data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [gatewayId, period]);

  return { analytics, loading };
};